// cspell:disable

import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { WeightUnitSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { productCategoryIdFormat } from "@redotech/redo-product-id";
import { isEnumValue } from "@redotech/util/enum";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ProductCategorySchema } from "../attributes";

const ShopifyProductCategorySchema_ = ProductCategorySchema.extend({
  id: productCategoryIdFormat.schema.optional(),
  shopifyGid: z.string(),
  parentId: productCategoryIdFormat.schema.nullish(),
});
export const ShopifyProductCategorySchema: z.ZodType<ShopifyProductCategory> =
  ShopifyProductCategorySchema_;
export interface ShopifyProductCategory extends z.infer<
  typeof ShopifyProductCategorySchema_
> {}

export enum UnitPriceMeasurementMeasuredUnit {
  CL = "CL", // 100 centiliters equals 1 liter
  CM = "CM", // 100 centimeters equals 1 meter
  FLOZ = "FLOZ", // Imperial fluid ounce (U.S. customary unit)
  FT = "FT", // 1 foot equals 12 inches
  FT2 = "FT2", // Imperial square foot
  G = "G", // Metric gram
  GAL = "GAL", // 1 gallon equals 128 fluid ounces (U.S. customary unit)
  IN = "IN", // Imperial inch
  ITEM = "ITEM", // 1 item, a unit of count
  KG = "KG", // 1 kilogram equals 1000 grams
  L = "L", // Metric liter
  LB = "LB", // Imperial pound
  M = "M", // Metric meter
  M2 = "M2", // Metric square meter
  M3 = "M3", // 1 cubic meter equals 1000 liters
  MG = "MG", // 1000 milligrams equals 1 gram
  ML = "ML", // 1000 milliliters equals 1 liter
  MM = "MM", // 1000 millimeters equals 1 meter
  OZ = "OZ", // 16 ounces equals 1 pound
  PT = "PT", // 1 pint equals 16 fluid ounces (U.S. customary unit)
  QT = "QT", // 1 quart equals 32 fluid ounces (U.S. customary unit)
  UNKNOWN = "UNKNOWN", // Unknown unit of measurement
  YD = "YD", // 1 yard equals 36 inches
}
export const UnitPriceMeasurementMeasuredUnitSchema = createEnum(
  UnitPriceMeasurementMeasuredUnit,
  { enumName: "UnitPriceMeasurementMeasuredUnit" },
);

export enum ShopifyWeightUnit {
  GRAMS = "GRAMS",
  KILOGRAMS = "KILOGRAMS",
  POUNDS = "POUNDS",
  OUNCES = "OUNCES",
}
const ShopifyWeightUnitSchema_ = createEnum(ShopifyWeightUnit, {
  enumName: "ShopifyWeightUnit",
});

export const ShopifyWeightUnitSchema = z
  .union([WeightUnitSchema, ShopifyWeightUnitSchema_])
  .transform((value): ShopifyWeightUnit => {
    if (isEnumValue(value, ShopifyWeightUnit)) {
      return value;
    }
    switch (value) {
      case WeightUnit.G:
        return ShopifyWeightUnit.GRAMS;
      case WeightUnit.Kg:
        return ShopifyWeightUnit.KILOGRAMS;
      case WeightUnit.Oz:
        return ShopifyWeightUnit.OUNCES;
      case WeightUnit.Lb:
        return ShopifyWeightUnit.POUNDS;
      default:
        throw new Error("Invalid weight unit");
    }
  });

const ShopifyWeightSchema_ = z.object({
  value: z.number(),
  unit: ShopifyWeightUnitSchema,
});
export const ShopifyWeightSchema: z.ZodType<ShopifyWeight> =
  ShopifyWeightSchema_;
export interface ShopifyWeight extends z.infer<typeof ShopifyWeightSchema_> {}

export enum CountryCode {
  AC = "AC", // Ascension Island
  AD = "AD", // Andorra
  AE = "AE", // United Arab Emirates
  AF = "AF", // Afghanistan
  AG = "AG", // Antigua & Barbuda
  AI = "AI", // Anguilla
  AL = "AL", // Albania
  AM = "AM", // Armenia
  AN = "AN", // Netherlands Antilles
  AO = "AO", // Angola
  AR = "AR", // Argentina
  AT = "AT", // Austria
  AU = "AU", // Australia
  AW = "AW", // Aruba
  AX = "AX", // Åland Islands
  AZ = "AZ", // Azerbaijan
  BA = "BA", // Bosnia & Herzegovina
  BB = "BB", // Barbados
  BD = "BD", // Bangladesh
  BE = "BE", // Belgium
  BF = "BF", // Burkina Faso
  BG = "BG", // Bulgaria
  BH = "BH", // Bahrain
  BI = "BI", // Burundi
  BJ = "BJ", // Benin
  BL = "BL", // St. Barthémy */
  BM = "BM", // Bermuda */
  BN = "BN", // Brunei
  BO = "BO", // Bolivia
  BQ = "BQ", // Caribbean Netherlands
  BR = "BR", // Brazil
  BS = "BS", // Bahamas
  BT = "BT", // Bhutan
  BV = "BV", // Bouvet Island
  BW = "BW", // Botswana
  BY = "BY", // Belarus
  BZ = "BZ", // Belize
  CA = "CA", // Canada
  CC = "CC", // Cocos (Keeling) Islands
  CD = "CD", // Congo - Kinshasa
  CF = "CF", // Central African Republic
  CG = "CG", // Congo - Brazzaville
  CH = "CH", // Switzerland
  CI = "CI", // Côte d'Ivoire
  CK = "CK", // Cook Islands
  CL = "CL", // Chile
  CM = "CM", // Cameroon
  CN = "CN", // China
  CO = "CO", // Colombia
  CR = "CR", // Costa Rica
  CU = "CU", // Cuba
  CV = "CV", // Cape Verde
  CW = "CW", // Curaçao
  CX = "CX", // Christmas Island
  CY = "CY", // Cyprus
  CZ = "CZ", // Czechia
  DE = "DE", // Germany
  DJ = "DJ", // Djibouti
  DK = "DK", // Denmark
  DM = "DM", // Dominica
  DO = "DO", // Dominican Republic
  DZ = "DZ", // Algeria
  EC = "EC", // Ecuador
  EE = "EE", // Estonia
  EG = "EG", // Egypt
  EH = "EH", // Western Sahara
  ER = "ER", // Eritrea
  ES = "ES", // Spain
  ET = "ET", // Ethiopia
  FI = "FI", // Finland
  FJ = "FJ", // Fiji
  FK = "FK", // Falkland Islands
  FO = "FO", // Faroe Islands
  FR = "FR", // France
  GA = "GA", // Gabon
  GB = "GB", // United Kingdom
  GD = "GD", // Grenada
  GE = "GE", // Georgia
  GF = "GF", // French Guiana
  GG = "GG", // Guernsey
  GH = "GH", // Ghana
  GI = "GI", // Gibraltar
  GL = "GL", // Greenland
  GM = "GM", // Gambia
  GN = "GN", // Guinea
  GP = "GP", // Guadeloupe
  GQ = "GQ", // Equatorial Guinea
  GR = "GR", // Greece
  GS = "GS", // South Georgia & South Sandwich Islands
  GT = "GT", // Guatemala
  GW = "GW", // Guinea-Bissau
  GY = "GY", // Guyana
  HK = "HK", // Hong Kong SAR
  HM = "HM", // Heard & McDonald Islands
  HN = "HN", // Honduras
  HR = "HR", // Croatia
  HT = "HT", // Haiti
  HU = "HU", // Hungary
  ID = "ID", // Indonesia
  IE = "IE", // Ireland
  IL = "IL", // Israel
  IM = "IM", // Isle of Man
  IN = "IN", // India
  IO = "IO", // British Indian Ocean Territory
  IQ = "IQ", // Iraq
  IR = "IR", // Iran
  IS = "IS", // Iceland
  IT = "IT", // Italy
  JE = "JE", // Jersey
  JM = "JM", // Jamaica
  JO = "JO", // Jordan
  JP = "JP", // Japan
  KE = "KE", // Kenya
  KG = "KG", // Kyrgyzstan
  KH = "KH", // Cambodia
  KI = "KI", // Kiribati
  KM = "KM", // Comoros
  KN = "KN", // St. Kitts & Nevis
  KP = "KP", // North Korea
  KR = "KR", // South Korea
  KW = "KW", // Kuwait
  KY = "KY", // Cayman Islands
  KZ = "KZ", // Kazakhstan
  LA = "LA", // Laos
  LB = "LB", // Lebanon
  LC = "LC", // St. Lucia
  LI = "LI", // Liechtenstein
  LK = "LK", // Sri Lanka
  LR = "LR", // Liberia
  LS = "LS", // Lesotho
  LT = "LT", // Lithuania
  LU = "LU", // Luxembourg
  LV = "LV", // Latvia
  LY = "LY", // Libya
  MA = "MA", // Morocco
  MC = "MC", // Monaco
  MD = "MD", // Moldova
  ME = "ME", // Montenegro
  MF = "MF", // St. Martin
  MG = "MG", // Madagascar
  MK = "MK", // North Macedonia
  ML = "ML", // Mali
  MM = "MM", // Myanmar (Burma)
  MN = "MN", // Mongolia
  MO = "MO", // Macao SAR
  MQ = "MQ", // Martinique
  MR = "MR", // Mauritania
  MS = "MS", // Montserrat
  MT = "MT", // Malta
  MU = "MU", // Mauritius
  MV = "MV", // Maldives
  MW = "MW", // Malawi
  MX = "MX", // Mexico
  MY = "MY", // Malaysia
  MZ = "MZ", // Mozambique
  NA = "NA", // Namibia
  NC = "NC", // New Caledonia
  NE = "NE", // Niger
  NF = "NF", // Norfolk Island
  NG = "NG", // Nigeria
  NI = "NI", // Nicaragua
  NL = "NL", // Netherlands
  NO = "NO", // Norway
  NP = "NP", // Nepal
  NR = "NR", // Nauru
  NU = "NU", // Niue
  NZ = "NZ", // New Zealand
  OM = "OM", // Oman
  PA = "PA", // Panama
  PE = "PE", // Peru
  PF = "PF", // French Polynesia
  PG = "PG", // Papua New Guinea
  PH = "PH", // Philippines
  PK = "PK", // Pakistan
  PL = "PL", // Poland
  PM = "PM", // St. Pierre & Miquelon
  PN = "PN", // Pitcairn Islands
  PS = "PS", // Palestinian Territories
  PT = "PT", // Portugal
  PY = "PY", // Paraguay
  QA = "QA", // Qatar
  RE = "RE", // Réunion
  RO = "RO", // Romania
  RS = "RS", // Serbia
  RU = "RU", // Russia
  RW = "RW", // Rwanda
  SA = "SA", // Saudi Arabia
  SB = "SB", // Solomon Islands
  SC = "SC", // Seychelles
  SD = "SD", // Sudan
  SE = "SE", // Sweden
  SG = "SG", // Singapore
  SH = "SH", // St. Helena
  SI = "SI", // Slovenia
  SJ = "SJ", // Svalbard & Jan Mayen
  SK = "SK", // Slovakia
  SL = "SL", // Sierra Leone
  SM = "SM", // San Marino
  SN = "SN", // Senegal
  SO = "SO", // Somalia
  SR = "SR", // Suriname
  SS = "SS", // South Sudan
  ST = "ST", // São Tomé & Príncipe
  SV = "SV", // El Salvador
  SX = "SX", // Sint Maarten
  SY = "SY", // Syria
  SZ = "SZ", // Eswatini
  TA = "TA", // Tristan da Cunha
  TC = "TC", // Turks & Caicos Islands
  TD = "TD", // Chad
  TF = "TF", // French Southern Territories
  TG = "TG", // Togo
  TH = "TH", // Thailand
  TJ = "TJ", // Tajikistan
  TK = "TK", // Tokelau
  TL = "TL", // Timor-Leste
  TM = "TM", // Turkmenistan
  TN = "TN", // Tunisia
  TO = "TO", // Tonga
  TR = "TR", // Turkey
  TT = "TT", // Trinidad & Tobago
  TV = "TV", // Tuvalu
  TW = "TW", // Taiwan
  TZ = "TZ", // Tanzania
  UA = "UA", // Ukraine
  UG = "UG", // Uganda
  UM = "UM", // U.S. Outlying Islands
  US = "US", // United States
  UY = "UY", // Uruguay
  UZ = "UZ", // Uzbekistan
  VA = "VA", // Vatican City
  VC = "VC", // St. Vincent & Grenadines
  VE = "VE", // Venezuela
  VG = "VG", // British Virgin Islands
  VN = "VN", // Vietnam
  VU = "VU", // Vanuatu
  WF = "WF", // Wallis & Futuna
  WS = "WS", // Samoa
  XK = "XK", // Kosovo
  YE = "YE", // Yemen
  YT = "YT", // Mayotte
  ZA = "ZA", // South Africa
  ZM = "ZM", // Zambia
  ZW = "ZW", // Zimbabwe
  ZZ = "ZZ", // Unknown Region
}
export const CountryCodeSchema = createEnum(CountryCode, {
  enumName: "CountryCode",
});
